import { Component, inject, OnInit } from '@angular/core'
import { CommonModule, DatePipe } from '@angular/common'
import { ActivatedRoute, Router } from '@angular/router'
import { TableModule } from 'primeng/table'
import { TagModule } from 'primeng/tag'
import { CardModule } from 'primeng/card'
import { ButtonModule } from 'primeng/button'
import { TooltipModule } from 'primeng/tooltip'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { InputTextModule } from 'primeng/inputtext'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import {
  DiagnosticAffair,
  DiagnosticListResponse,
  DiagnosticReasonCount,
  OptimizationDiagnosticService,
} from '../../services/optimization-diagnostic.service'

type Severity = 'success' | 'warn' | 'danger' | 'info' | 'secondary'

@Component({
  selector: 'app-optimization-diagnostic',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    TableModule,
    TagModule,
    CardModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
  ],
  templateUrl: './optimization-diagnostic.component.html',
  styleUrl: './optimization-diagnostic.component.scss',
})
export class OptimizationDiagnosticComponent implements OnInit {
  private service = inject(OptimizationDiagnosticService)
  private router = inject(Router)
  private route = inject(ActivatedRoute)

  public loading$ = this.service.loading$

  public data: DiagnosticListResponse | null = null
  public affairs: DiagnosticAffair[] = []
  public activeCode: string | null = null
  public searchTerm = ''
  public copiedAffairId: number | null = null
  public diagnosingId: number | null = null

  private runId?: number

  ngOnInit() {
    const param = this.route.snapshot.queryParamMap.get('runId')
    this.runId = param ? Number(param) : undefined
    this.load()
  }

  load() {
    this.service.getDiagnostics(this.runId).subscribe((res) => {
      this.data = res
      this.applyFilter()
    })
  }

  refresh() {
    this.load()
  }

  toggleReason(reason: DiagnosticReasonCount) {
    this.activeCode = this.activeCode === reason.code ? null : reason.code
    this.applyFilter()
  }

  onSearchChange(value: string) {
    this.searchTerm = value
    this.applyFilter()
  }

  private applyFilter() {
    const all = this.data?.affairs ?? []
    let filtered = this.activeCode
      ? all.filter((a) => a.code === this.activeCode)
      : all

    const term = this.searchTerm.trim().toLowerCase()
    if (term) {
      filtered = filtered.filter((a) =>
        [
          a.name,
          a.groupingName,
          String(a.affairId),
          a.postalCode,
          a.locality,
          a.mapsQuery,
        ].some((field) => field?.toLowerCase().includes(term))
      )
    }

    this.affairs = filtered
  }

  openAffair(affair: DiagnosticAffair) {
    // La chronologie n'existe qu'une fois l'affaire diagnostiquée : tant qu'elle
    // ne l'est pas, le clic sur la ligne ne fait rien (le diagnostic se lance via
    // le bouton dédié, pour éviter une sonde VROOM coûteuse par clic accidentel).
    if (!affair.code) {
      return
    }
    this.router.navigate(
      ['/admin/optimization-diagnostic', affair.affairId],
      this.runId ? { queryParams: { runId: this.runId } } : {}
    )
  }

  /** Lance le diagnostic à la demande d'une affaire et met à jour la ligne. */
  runDiagnostic(affair: DiagnosticAffair, event?: MouseEvent) {
    event?.stopPropagation()
    if (this.diagnosingId !== null) return
    this.diagnosingId = affair.affairId
    const runId = this.runId ?? this.data?.run?.id
    this.service.runAffairDiagnostic(affair.affairId, runId).subscribe({
      next: (res) => {
        affair.code = res.affair.code
        affair.label = res.affair.label
        affair.detail = res.affair.detail
        affair.comment = res.affair.comment
        affair.finalizedAt = res.affair.finalizedAt
        this.recomputeReasons()
      },
      complete: () => {
        if (this.diagnosingId === affair.affairId) this.diagnosingId = null
      },
      error: () => {
        if (this.diagnosingId === affair.affairId) this.diagnosingId = null
      },
    })
  }

  /** Recalcule la répartition par raison depuis les affaires déjà diagnostiquées. */
  private recomputeReasons() {
    if (!this.data) return
    const counts = new Map<string, DiagnosticReasonCount>()
    for (const a of this.data.affairs) {
      if (!a.code) continue
      const existing = counts.get(a.code)
      if (existing) {
        existing.count++
      } else {
        counts.set(a.code, {
          code: a.code,
          label: a.label ?? a.code,
          count: 1,
        })
      }
    }
    this.data.reasons = [...counts.values()].sort((x, y) => y.count - x.count)
    this.applyFilter()
  }

  undiagnosedCount(): number {
    return (this.data?.affairs ?? []).filter((a) => !a.code).length
  }

  copyMapsQuery(affair: DiagnosticAffair, event: MouseEvent) {
    event.stopPropagation()
    const text = affair.mapsQuery ?? this.coordsString(affair)
    if (!text) return
    navigator.clipboard.writeText(text).then(() => {
      this.copiedAffairId = affair.affairId
      setTimeout(() => {
        if (this.copiedAffairId === affair.affairId) this.copiedAffairId = null
      }, 1500)
    })
  }

  openMaps(affair: DiagnosticAffair, event: MouseEvent) {
    event.stopPropagation()
    const query = affair.mapsQuery ?? this.coordsString(affair)
    if (!query) return
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    window.open(url, '_blank')
  }

  openRedirect(affair: DiagnosticAffair, event: MouseEvent) {
    event.stopPropagation()
    if (affair.redirectUrl) window.open(affair.redirectUrl, '_blank')
  }

  private coordsString(affair: DiagnosticAffair): string | null {
    return affair.latitude != null && affair.longitude != null
      ? `${affair.latitude},${affair.longitude}`
      : null
  }

  coveragePercent(): number | null {
    const run = this.data?.run
    if (!run || !run.affairsCount || run.affairsWithSlotsCount == null)
      return null
    return Math.round((run.affairsWithSlotsCount / run.affairsCount) * 100)
  }

  reasonSeverity(code: string | null): Severity {
    switch (code) {
      case 'missing_coords':
      case 'no_tech_in_radius':
      case 'no_tech_with_skills':
      case 'vroom_unroutable':
      case 'vroom_error':
        return 'danger'
      case 'deadline_passed':
      case 'no_tech_available':
      case 'unassigned':
        return 'warn'
      case 'crowded_out':
        return 'info'
      default:
        return 'secondary'
    }
  }
}
